/* CUSTOM CODE */

/*
 * <zoom-pan> — per-image zoom + pan inside the product media modal.
 *
 * Desktop (fine pointer): click toggles fit <-> zoom (focused on click point);
 *   while zoomed, drag to pan. Click again to reset to fit.
 * Touch (coarse pointer): pinch to zoom (1x–5x), double-tap toggles fit <-> 2x,
 *   single-finger drag pans while zoomed. At 1x, vertical swipe scrolls the gallery.
 *
 * Transform model: `translate(x,y) scale(s)` with center origin (set via CSS vars
 * --zoom-x / --zoom-y / --zoom-scale on the element).
 */
class ZoomPan extends HTMLElement {
  constructor() {
    super();
    this.img = this.querySelector('img');
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this.minScale = 1;
    this.maxScale = 5;
    this.zoomScale = 1.85; // touch double-tap target + minimum desktop zoom floor
    this.zoomExtra = 1.29; // beyond "cover" so BOTH axes overflow and can pan (~15% more zoom)
    this.zoomCoverMax = 2.75; // cap so narrow images don't blow up too large
    this.zoomPad = 56; // px of horizontal over-travel so left/right edges clear with a margin
    this.zoomPadY = 140; // more vertical over-travel so top/bottom show further past the edges
    this.isTouch = window.matchMedia('(pointer: coarse)').matches;
  }

  connectedCallback() {
    if (this.bound || !this.img) return;
    this.bound = true;
    this.addEventListener('dragstart', (e) => e.preventDefault());
    this.#trackLoading();
    if (this.isTouch) this.#initTouch();
    else this.#initMouse();
  }

  // Show a spinner until the (often large) image has loaded.
  #trackLoading() {
    const done = () => this.classList.add('is-loaded');
    if (this.img.complete && this.img.naturalWidth) {
      done();
    } else {
      this.img.addEventListener('load', done, { once: true });
      this.img.addEventListener('error', done, { once: true });
    }
  }

  // Kick the image request without waiting for lazy-load (start fetching now).
  preload() {
    if (this.img && this.img.loading === 'lazy') this.img.loading = 'eager';
  }

  // Called when this slide becomes active: show the spinner right away if the
  // image isn't ready yet, and make sure it's fetching.
  ensureLoad() {
    if (!this.img) return;
    this.preload();
    if (this.img.complete && this.img.naturalWidth) {
      this.classList.add('is-loaded');
    } else {
      this.classList.remove('is-loaded'); // spinner visible immediately
      this.img.addEventListener('load', () => this.classList.add('is-loaded'), { once: true });
      this.img.addEventListener('error', () => this.classList.add('is-loaded'), { once: true });
    }
  }

  /* ---------- shared helpers ---------- */

  apply() {
    this.style.setProperty('--zoom-scale', String(this.scale));
    this.style.setProperty('--zoom-x', `${this.x}px`);
    this.style.setProperty('--zoom-y', `${this.y}px`);
    const zoomed = this.scale > 1.001;
    this.classList.toggle('is-zoomed', zoomed);
    const modal = this.closest('.product-media-modal--zoom');
    if (modal) modal.classList.toggle('pm-has-zoom', zoomed);
  }

  reset() {
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    this._panBounds = null;
    if (this._panRAF) {
      cancelAnimationFrame(this._panRAF);
      this._panRAF = null;
    }
    this.classList.remove('is-grabbing');
    this.classList.remove('is-panning');
    this.apply();
  }

  // Enter zoom: scale the image so it COVERS the viewport (no letterbox), so it
  // overflows and can pan; then follow the cursor. Fashion Nova style.
  #enterZoom(clientX, clientY) {
    this._lastX = clientX;
    this._lastY = clientY;
    this.scale = 1.0001; // trip the zoomed state so the box grows to the viewport
    this.apply();
    requestAnimationFrame(() => {
      const rect = this.getBoundingClientRect();
      const iw = this.img.offsetWidth;
      const ih = this.img.offsetHeight;
      if (!iw || !ih || !rect.width) {
        this.scale = this.zoomScale;
      } else {
        const cover = Math.max(rect.width / iw, rect.height / ih);
        this.scale = Math.min(this.zoomCoverMax, Math.max(this.zoomScale, cover * this.zoomExtra));
      }
      this.#cachePanBounds();
      this.#magnifier(clientX, clientY, false);
    });
  }

  // Cache pan geometry once (reads layout) so pointermove never triggers a reflow.
  #cachePanBounds() {
    const rect = this.getBoundingClientRect();
    const iw = this.img.offsetWidth * this.scale;
    const ih = this.img.offsetHeight * this.scale;
    this._panBounds = {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
      maxX: Math.max(0, (iw - rect.width) / 2) + this.zoomPad,
      maxY: Math.max(0, (ih - rect.height) / 2) + this.zoomPadY,
    };
  }

  // Magnifier pan: pure math on cached bounds — no layout reads, so it's smooth.
  #magnifier(clientX, clientY, instant) {
    const b = this._panBounds;
    if (!b) return;
    const fx = Math.min(1, Math.max(0, (clientX - b.left) / b.width));
    const fy = Math.min(1, Math.max(0, (clientY - b.top) / b.height));
    this.x = b.maxX * (1 - 2 * fx);
    this.y = b.maxY * (1 - 2 * fy);
    if (instant) this.classList.add('is-panning'); // kill transition for instant follow
    this.apply();
  }

  #clamp() {
    const rect = this.getBoundingClientRect();
    const iw = this.img.clientWidth * this.scale;
    const ih = this.img.clientHeight * this.scale;
    const maxX = Math.max(0, (iw - rect.width) / 2);
    const maxY = Math.max(0, (ih - rect.height) / 2);
    this.x = Math.min(maxX, Math.max(-maxX, this.x));
    this.y = Math.min(maxY, Math.max(-maxY, this.y));
  }

  // Zoom toward a screen point, keeping that point roughly stationary.
  #zoomTo(scale, clientX, clientY) {
    const rect = this.getBoundingClientRect();
    const cx = clientX - (rect.left + rect.width / 2);
    const cy = clientY - (rect.top + rect.height / 2);
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, scale));
    this.x = -cx * (this.scale - 1);
    this.y = -cy * (this.scale - 1);
    this.#clamp();
    this.apply();
  }

  /* ---------- desktop: click toggle + magnifier (mouse-move) pan ---------- */

  #initMouse() {
    let downX = 0;
    let downY = 0;

    this.addEventListener('pointerdown', (e) => {
      downX = e.clientX;
      downY = e.clientY;
    });

    // while zoomed, the image follows the cursor — throttled to one update per frame
    this.addEventListener('pointermove', (e) => {
      if (this.scale <= 1 || !this._panBounds) return;
      this._lastX = e.clientX;
      this._lastY = e.clientY;
      if (this._panRAF) return;
      this._panRAF = requestAnimationFrame(() => {
        this._panRAF = null;
        this.#magnifier(this._lastX, this._lastY, true);
      });
    });

    this.addEventListener('pointerup', (e) => {
      e.stopPropagation(); // prevent the modal's media-modal auto-close on mouse up
      // treat a small press as a click (ignore accidental drags)
      if (Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY) > 6) return;
      if (this.scale > 1) {
        this.reset();
      } else {
        this.#enterZoom(e.clientX, e.clientY); // cover-fill zoom, then follow cursor
      }
    });

    // keep the framing correct if the window resizes while zoomed
    window.addEventListener('resize', () => {
      if (this.scale > 1) {
        this.#cachePanBounds();
        this.#magnifier(this._lastX, this._lastY, true);
      }
    });
  }

  /* ---------- touch: pinch + double-tap + drag ---------- */

  #initTouch() {
    let startDist = 0;
    let startScale = 1;
    let dragging = false;
    let sx = 0;
    let sy = 0;
    let ox = 0;
    let oy = 0;
    let lastTap = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    const dist = (a, b) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

    this.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length === 2) {
          startDist = dist(e.touches[0], e.touches[1]);
          startScale = this.scale;
          dragging = false;
          e.preventDefault();
          return;
        }
        const t = e.touches[0];
        const now = performance.now();
        // double-tap detection
        if (now - lastTap < 300 && Math.hypot(t.clientX - lastTapX, t.clientY - lastTapY) < 50) {
          if (this.scale > 1) this.reset();
          else this.#zoomTo(this.zoomScale, t.clientX, t.clientY);
          lastTap = 0;
          e.preventDefault();
          return;
        }
        lastTap = now;
        lastTapX = t.clientX;
        lastTapY = t.clientY;
        if (this.scale > 1) {
          dragging = true;
          sx = t.clientX;
          sy = t.clientY;
          ox = this.x;
          oy = this.y;
          this.#cachePanBounds(); // measure once, not every move
          this.classList.add('is-panning'); // kill the transition -> no drag resistance
        }
      },
      { passive: false }
    );

    this.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const d = dist(e.touches[0], e.touches[1]);
          const mid = {
            clientX: (e.touches[0].clientX + e.touches[1].clientX) / 2,
            clientY: (e.touches[0].clientY + e.touches[1].clientY) / 2,
          };
          this.#zoomTo((d / startDist) * startScale, mid.clientX, mid.clientY);
          return;
        }
        if (dragging && this.scale > 1) {
          e.preventDefault(); // pan instead of scrolling the gallery
          const t = e.touches[0];
          this.x = ox + (t.clientX - sx);
          this.y = oy + (t.clientY - sy);
          const b = this._panBounds;
          if (b) {
            this.x = Math.min(b.maxX, Math.max(-b.maxX, this.x));
            this.y = Math.min(b.maxY, Math.max(-b.maxY, this.y));
          }
          this.apply();
        }
        // scale === 1: let the touch scroll the gallery normally
      },
      { passive: false }
    );

    this.addEventListener('touchend', (e) => {
      if (e.touches.length === 0) {
        dragging = false;
        this.classList.remove('is-panning');
      }
    });
  }
}

if (!customElements.get('zoom-pan')) {
  customElements.define('zoom-pan', ZoomPan);
}
